import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { Navigation } from 'app/core/navigation/navigation.types';
import { Observable, tap } from 'rxjs';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { distinctUntilChanged, filter, map, skip } from 'rxjs/operators';
import { FuseNavigationItem } from '../../../@fuse/components/navigation';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import type { Module } from '../../features/admin/modules/modules.model';
import { cloneDeep } from 'lodash-es';
import { adminNavigation, defaultNavigation } from './data';
import { UtilityService } from '../../shared/services/utility.service';
import { TitleService } from '../../shared/services/title.service';
import { ModulesStore } from '../../features/admin/modules/modules.store';
import { UserStore } from '../services/user.store';
import { UserRolesStore } from '../../features/admin/user-roles/user-roles.store';

@Injectable({ providedIn: 'root' })
export class NavigationService {
  readonly #modulesStore = inject(ModulesStore);
  readonly #userStore = inject(UserStore);
  readonly #userRolesStore = inject(UserRolesStore);
  navigation = signal<Navigation>({ default: [], admin: [] });
  filteredNavigation = signal<Navigation>({ default: [], admin: [] });
  currentTitle: string;
  currentPageIsPublic = signal(false);
  route = inject(ActivatedRoute);
  params = toSignal(this.route.paramMap);
  id = computed(() => {
    return this.params()?.get('name');
  });

  /**
   * Constructor
   */
  constructor(
    private _router: Router,
    private title: TitleService
  ) {
    _router.events
      .pipe(
        takeUntilDestroyed(),
        filter(event => event instanceof NavigationStart)
      )
      .subscribe(() => {
        this.currentTitle = this.title.getTitle();
        this.currentPageIsPublic.set(this.#modulesStore.getModuleBySlug(UtilityService.getSlugOfUrl())?.isPublic);
      });

    this.currentPageIsPublic.set(this.#modulesStore.getModuleBySlug(UtilityService.getSlugOfUrl())?.isPublic);

    // Reactive effect that automatically re-runs when any tracked signal changes
    effect(() => {
      const modulesLoaded = this.#modulesStore.modulesLoaded();
      const permissions = this.#userStore.currentUserPermissions();
      const enabledPages = this.#modulesStore.enabledPages();
      const enabledEntities = this.#modulesStore.enabledEntities();

      if (modulesLoaded && permissions) {
        this.updateMenu(enabledPages, enabledEntities);
      }
    });

    // Refresh user permissions when role permissions actually change (not just array reference)
    toObservable(this.#userRolesStore.userRoles)
      .pipe(
        takeUntilDestroyed(),
        filter(roles => roles?.length > 0),
        map(roles => {
          const currentUserRoleIds = this.#userStore.currentUserRoles();
          if (!currentUserRoleIds?.length) return '';
          return roles
            .filter(r => currentUserRoleIds.includes(r.id))
            .map(r => `${r.id}:${r.permissions?.join(',')}`)
            .join('|');
        }),
        distinctUntilChanged(),
        skip(1),
        filter(() => this.#userStore.loaded())
      )
      .subscribe(() => {
        this.#userStore.refreshCurrentUser();
      });
  }

  createMenuItem(module: Module): FuseNavigationItem {
    const queryParams = {
      ...module.defaultFilters,
      sortDirection: module?.defaultSortDirection ? module.defaultSortDirection : null,
      sort: module.defaultSort ? module.defaultSort : null,
    };
    return {
      id: module.slug,
      title: module.title,
      category: module.category,
      menuOrder: module.menuOrder,
      subMenuOrder: module.subMenuOrder,
      type: 'basic',
      link: module.slug,
      queryParams: queryParams,
    };
  }

  updateMenu(pages: Module[], modules: Module[]) {
    const menuItemsPages = pages
      .filter(page => {
        return (
          this.#userStore.checkIfHasReadPermission(page.id) &&
          page.showMainMenu &&
          !this.#userStore.checkIfMenuItemIsHiddenForUserRole(page.id)
        );
      })
      .map(page => {
        return this.createMenuItem(page);
      });
    const menuItemsModules = modules
      .filter(module => {
        return (
          this.#userStore.checkIfHasReadPermission(module.id) &&
          module.showMainMenu &&
          !this.#userStore.checkIfMenuItemIsHiddenForUserRole(module.id)
        );
      })
      .map(module => {
        return this.createMenuItem(module);
      });

    const menuItemAdminConfiguration: FuseNavigationItem[] = this.#userStore.isAdmin()
      ? [
          {
            id: 'beheerders-instellingen',
            title: 'Beheerders Instellingen',
            category: 'Beheerders Instellingen',
            menuOrder: 1000,
            subMenuOrder: 1000,
            classes: {
              wrapper: 'menu-item--return',
            },
            type: 'basic',
            icon: 'mat_outline:admin_panel_settings',
            link: '/beheerders-instellingen/modules',
          },
        ]
      : [];

    const menuItemsWithSubmenus = this.createSubmenus([
      ...menuItemsPages,
      ...menuItemsModules,
      ...menuItemAdminConfiguration,
    ]);

    const navigationsCopy = cloneDeep(this.navigation());
    navigationsCopy.default = menuItemsWithSubmenus;
    this.filteredNavigation.set(navigationsCopy);
  }

  createSubmenus(menuItems: FuseNavigationItem[]) {
    const categories: { [key: string]: FuseNavigationItem[] } = {};

    const sortedMenuItems = UtilityService.sortArrayByProperty(menuItems, 'menuOrder');

    sortedMenuItems.forEach(menuItem => {
      if (categories[menuItem.category] === undefined) {
        categories[menuItem.category] = [];
      }
      categories[menuItem.category].push(menuItem);
    });

    return Object.keys(categories).map(category => {
      if (categories[category].length === 1) {
        return categories[category][0];
      } else {
        const sortedSubMenuItems = UtilityService.sortArrayByProperty(categories[category], 'subMenuOrder');

        return {
          id: categories[category][0].category,
          title: categories[category][0].category,
          type: 'collapsable',
          children: sortedSubMenuItems.map(menuItem => {
            return {
              id: categories[category][0].category + '.' + menuItem.id,
              title: menuItem.title,
              type: 'basic',
              link: menuItem.link,
              queryParams: menuItem.queryParams,
              queryParamsHandling: '',
            };
          }),
        } as FuseNavigationItem;
      }
    });
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Get all navigation data
   */
  get(): Navigation {
    const navigation: Navigation = {
      default: cloneDeep(defaultNavigation),
      admin: cloneDeep(adminNavigation),
    };
    this.navigation.set(navigation);
    return navigation;
  }

  set(navigation: Navigation) {
    this.filteredNavigation.set(navigation);
  }
}
